import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    ActivityStreamSummary,
    Alarm,
    AlarmFormInterface,
    AllSegmentsIncludingCustom,
    AssetsMostOwned,
    CryptoFiatStableMetricDaily,
    DailyTraderAccounts,
    HoldingsSummary,
    HourlyOpenPosition,
    IntelligenceState,
    LastOpenPosition,
    LastTraderAccount,
    OpenPositionMetricSummary,
    ScalperActivityData,
    SegmentHoldingsSummary,
    TopCrypto,
    TopListAsset,
} from '@/state/intelligence2/intelligence2Types';

const initialState: IntelligenceState = {
    lastOpenPosition: null,
    hourlyOpenPosition: null,
    openPositionSelectValue: { label: '24h', value: '24' },
    top10Perps: null,
    lastTraderAccount: null,
    dailyTraderAccounts: null,
    connectedTradersSelectValue: { label: '30d', value: '30' },
    allSegmentsIncludingCustom: [],
    segmentsAssetsById: null,
    scalperActivityData: null,
    assetsMostOwned: null,
    topCrypto: null,
    alarms: [],
    alarmFormInterface: null,
    updatedAlarm: null,
    holdingsSummary: null,
    cryptoFiatStableMetricDaily: null,
    activityStreamSummary: {
        summary: { long: { open: 0, close: 0 }, short: { open: 0, close: 0 } },
        lastUpdate: null,
        nextUpdate: null,
    },
    segmentForEdit: undefined,
};

const slice = createSlice({
    name: 'intelligence2',
    initialState,
    reducers: {
        resetIntelligence: (state) => {
            state.lastOpenPosition = null;
            state.hourlyOpenPosition = null;
            state.openPositionSelectValue = { label: '24h', value: '24' };
            state.top10Perps = null;
            state.lastTraderAccount = null;
            state.dailyTraderAccounts = null;
            state.connectedTradersSelectValue = { label: '30d', value: '30' };
            state.allSegmentsIncludingCustom = [];
            state.segmentsAssetsById = null;
            state.scalperActivityData = null;
            state.assetsMostOwned = null;
            state.topCrypto = null;
            state.alarms = [];
            state.alarmFormInterface = null;
            state.updatedAlarm = null;
            state.holdingsSummary = null;
            state.cryptoFiatStableMetricDaily = null;
            state.segmentForEdit = undefined;
        },
        setTop10Perps: (state, action: PayloadAction<TopListAsset | null>) => {
            state.top10Perps = action.payload;
        },
        setLastOpenPosition: (state, action: PayloadAction<LastOpenPosition | null>) => {
            state.lastOpenPosition = action.payload;
        },
        setHourlyOpenPosition: (state, action: PayloadAction<HourlyOpenPosition | null>) => {
            state.hourlyOpenPosition = action.payload;
        },
        setLastTraderAccount: (state, action: PayloadAction<LastTraderAccount | null>) => {
            state.lastTraderAccount = action.payload;
        },
        setDailyTraderAccounts: (state, action: PayloadAction<DailyTraderAccounts | null>) => {
            state.dailyTraderAccounts = action.payload;
        },
        setOpenPositionSelectValue: (
            state,
            action: PayloadAction<{ label: string; value: string }>,
        ) => {
            state.openPositionSelectValue = action.payload;
        },
        setConnectedTradersSelectValue: (
            state,
            action: PayloadAction<{ label: string; value: string }>,
        ) => {
            state.connectedTradersSelectValue = action.payload;
        },
        setAllSegmentsIncludingCustom: (
            state,
            action: PayloadAction<AllSegmentsIncludingCustom[] | null>,
        ) => {
            state.allSegmentsIncludingCustom = action.payload || [];
        },

        setSegmentsAssetsById: (
            state,
            action: PayloadAction<{
                id: number;
                data: OpenPositionMetricSummary | SegmentHoldingsSummary;
            } | null>,
        ) => {
            if (action.payload) {
                state.segmentsAssetsById = {
                    ...state.segmentsAssetsById,
                    [action.payload.id]: action.payload.data,
                };
            }
        },

        setScalperActivityData: (state, action: PayloadAction<ScalperActivityData>) => {
            state.scalperActivityData = action.payload;
        },
        setAssetsMostOwned: (state, action: PayloadAction<AssetsMostOwned>) => {
            state.assetsMostOwned = action.payload;
        },

        setTopCrypto: (state, action: PayloadAction<TopCrypto | null>) => {
            state.topCrypto = action.payload;
        },
        setAlarms: (state, action: PayloadAction<Alarm[]>) => {
            state.alarms = action.payload;
        },
        updateAlarm: (state, action: PayloadAction<Alarm>) => {
            state.alarms = state.alarms.map((alarm) => {
                if (alarm.id === action.payload.id) {
                    return action.payload;
                }
                return alarm;
            });
        },
        addAlarm: (state, action: PayloadAction<Alarm>) => {
            state.alarms.push(action.payload);
        },
        removeAlarm: (state, action: PayloadAction<number>) => {
            state.alarms = state.alarms.filter((alarm) => alarm.id !== action.payload);
        },
        setUpdatedAlarm: (state, action: PayloadAction<Alarm | null>) => {
            state.updatedAlarm = action.payload;
        },
        setAlarmFormInterface: (state, action: PayloadAction<AlarmFormInterface | null>) => {
            state.alarmFormInterface = action.payload;
        },
        setHoldingsSummary: (state, action: PayloadAction<HoldingsSummary | null>) => {
            state.holdingsSummary = action.payload;
        },
        setCryptoFiatStable: (state, action: PayloadAction<CryptoFiatStableMetricDaily | null>) => {
            state.cryptoFiatStableMetricDaily = action.payload;
        },

        setActivityStreamSummary: (state, action: PayloadAction<ActivityStreamSummary>) => {
            state.activityStreamSummary = action.payload;
        },
        addCustomSegments: (state, action: PayloadAction<AllSegmentsIncludingCustom>) => {
            state.allSegmentsIncludingCustom.push(action.payload);
        },
        updateCustomSegments: (state, action: PayloadAction<AllSegmentsIncludingCustom>) => {
            state.allSegmentsIncludingCustom.map((element) => {
                if (element.id === action.payload.id) {
                    element.label = action.payload.label;
                }
                return element;
            });
        },
        removeCustomSegments: (state, action: PayloadAction<number>) => {
            state.allSegmentsIncludingCustom = state.allSegmentsIncludingCustom.filter(
                (element) => element.id !== action.payload,
            );
        },
        setSegmentForEdit: (
            state,
            action: PayloadAction<AllSegmentsIncludingCustom | undefined>,
        ) => {
            state.segmentForEdit = action.payload;
        },
    },
});

export const {
    setTop10Perps,
    setLastOpenPosition,
    setHourlyOpenPosition,
    setLastTraderAccount,
    setDailyTraderAccounts,
    setOpenPositionSelectValue,
    setConnectedTradersSelectValue,
    setAllSegmentsIncludingCustom,
    setSegmentsAssetsById,
    setScalperActivityData,
    setAssetsMostOwned,
    setTopCrypto,
    setAlarms,
    addAlarm,
    removeAlarm,
    setUpdatedAlarm,
    updateAlarm,
    setAlarmFormInterface,
    setHoldingsSummary,
    setCryptoFiatStable,
    setActivityStreamSummary,
    resetIntelligence,
    addCustomSegments,
    updateCustomSegments,
    removeCustomSegments,
    setSegmentForEdit,
} = slice.actions;

export default slice.reducer;
